import {
  Box,
  Grid,
  useToast,
  useDisclosure,
  VStack,
  HStack,
  Heading,
  Text,
  Avatar,
  Input,
  FormControl,
  FormLabel,
  Button,
  Select,
  Icon,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getMyUserProfile, checkToken, updateUser } from "../../../api/api";
import { MdCameraAlt, MdLock, MdChevronRight } from "react-icons/md";
import { useNavbarAction } from "contexts/NavbarActionContext";
import LoadingSpinner from "components/LoadingSpinner";
import ChangePasswordModal from "components/profile/ChangePasswordModal";

const SidebarItem = ({ children, icon, isActive, onClick }) => {
  const activeColor = "blue.600";
  const activeBg = "blue.50";
  const inactiveColor = "gray.600";

  return (
    <HStack
      px={4}
      py={3}
      spacing={3}
      cursor="pointer"
      onClick={onClick}
      bg={isActive ? activeBg : "transparent"}
      color={isActive ? activeColor : inactiveColor}
      borderRadius="lg"
      transition="all 0.2s"
      _hover={{ bg: isActive ? activeBg : "gray.50" }}
      position="relative"
    >
      {isActive && (
        <Box
          position="absolute"
          left={0}
          w="3px"
          h="16px"
          bg="blue.600"
          borderRadius="full"
        />
      )}
      <Text fontSize="sm" fontWeight={isActive ? "600" : "500"}>
        {isActive && "• "} {children}
      </Text>
    </HStack>
  );
};

export default function Overview() {
  const toast = useToast();
  const navigate = useNavigate();
  const { setUserData: setGlobalUserData } = useNavbarAction();
  const [activeSection, setActiveSection] = useState("email");
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const {
    isOpen: isChangePasswordOpen,
    onOpen: onChangePasswordOpen,
    onClose: onChangePasswordClose,
  } = useDisclosure();
  const [userData, setUserData] = useState({
    nom: "",
    prenom: "",
    email: "",
    rôle: "",
    téléphone: "",
    statut: "",
    photo: "",
    genre: "",
    date_naissance: "",
    ville: "",
    langue: "fr"
  });

  const sections = [
    { id: "email", label: "Informations personnelles" },
  ];

  // Liste des villes du Maroc
  const villesMaroc = [
    "Agadir", "Al Hoceïma", "Beni Mellal", "Berkane", "Casablanca", "Chefchaouen",
    "El Jadida", "Errachidia", "Essaouira", "Fès", "Guelmim", "Kénitra", "Khémisset",
    "Khouribga", "Laâyoune", "Larache", "Marrakech", "Meknès", "Mohammédia", "Nador",
    "Ouarzazate", "Oujda", "Rabat", "Safi", "Salé", "Settat", "Tanger", "Taroudant",
    "Taza", "Témara", "Tétouan", "Tiznit"
  ];

  // Vérifier la session via le cookie httpOnly `auth_token`
  useEffect(() => {
    let isMounted = true;

    const checkTokenAndUser = async () => {
      setIsLoading(true);
      try {
        // Le backend lit le cookie auth_token et vérifie sa validité
        const check = await checkToken();

        // Vérifier si le composant est toujours monté
        if (!isMounted) return;

        if (!check.data || !check.data.valid) {
          console.warn("Token invalide ou expiré");
          setIsAuthenticated(false);
          setIsLoading(false);
          navigate('/auth/sign-in', { replace: true });
          return;
        }

        // Si le token est valide, récupérer le profil utilisateur
        try {
          const { data: user } = await getMyUserProfile();

          if (!isMounted) return;

          if (user && user.id) {
            setUserData({
              ...user,
              ville: user.ville || "",
              date_naissance: user.date_naissance || "",
              langue: user.langue || "fr"
            });
            setIsAuthenticated(true);
          } else {
            // Utilisateur non trouvé mais token valide - utiliser les données du token
            console.warn("Profil utilisateur non trouvé, utilisation des données du token");
            if (check.data.user) {
              setUserData({
                ...check.data.user,
                ville: check.data.user.ville || "",
                date_naissance: check.data.user.date_naissance || "",
                langue: check.data.user.langue || "fr"
              });
            }
            setIsAuthenticated(true);
          }
        } catch (profileError) {
          if (!isMounted) return;

          // Si erreur 404 sur /users/me mais token valide, on reste authentifié
          // et on utilise les données du token si disponibles
          if (profileError.response?.status === 404 && check.data.user) {
            console.warn("Endpoint /users/me non trouvé, utilisation des données du token");
            setUserData({
              ...check.data.user,
              ville: check.data.user.ville || "",
              date_naissance: check.data.user.date_naissance || "",
              langue: check.data.user.langue || "francais"
            });
            setIsAuthenticated(true);
          } else if (profileError.response?.status === 401 || profileError.response?.status === 403) {
            // Erreur d'authentification réelle
            setIsAuthenticated(false);
            navigate('/auth/sign-in', { replace: true });
            return;
          } else {
            // Autre erreur (réseau, serveur) - afficher message mais rester sur la page
            console.error("Erreur lors de la récupération du profil:", profileError);
            toast({
              title: "Erreur",
              description: "Impossible de charger le profil. Veuillez réessayer.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            // On considère quand même l'utilisateur comme authentifié si le token est valide
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        if (!isMounted) return;

        console.error("Erreur de vérification du token:", error);

        // Seulement rediriger vers login si c'est une erreur d'authentification (401, 403)
        if (error.response?.status === 401 || error.response?.status === 403) {
          setIsAuthenticated(false);
          navigate('/auth/sign-in', { replace: true });
        } else if (error.code === 'ERR_NETWORK') {
          // Erreur réseau - afficher message mais ne pas rediriger
          toast({
            title: "Erreur de connexion",
            description: "Impossible de se connecter au serveur. Vérifiez votre connexion.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          setIsAuthenticated(false);
        } else {
          // Autre erreur - afficher message
          toast({
            title: "Erreur",
            description: "Une erreur est survenue. Veuillez réessayer.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          setIsAuthenticated(false);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    checkTokenAndUser();

    // Cleanup function pour éviter les updates sur un composant démonté
    return () => {
      isMounted = false;
    };
  }, [navigate, toast]);

  useEffect(() => {
    const scrollContainer = document.getElementById("profile-content-scroll");
    if (!scrollContainer) return;

    const observerOptions = {
      root: scrollContainer,
      rootMargin: '-20px 0px -70% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sections.forEach(section => {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [sections]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: "Erreur",
          description: "L'image ne doit pas dépasser 2 Mo",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setUserData(prev => ({
          ...prev,
          photo: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async (section) => {
    try {
      // Vérifier que l'ID utilisateur est disponible (déjà chargé au démarrage)
      if (!userData.id) {
        toast({
          title: "Erreur",
          description: "Session expirée, veuillez vous reconnecter",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        navigate('/auth/sign-in');
        return;
      }

      // Toujours envoyer du JSON (la photo est déjà en base64 dans userData.photo)
      const jsonData = {};
      Object.keys(userData).forEach(key => {
        // Ne jamais gérer le mot de passe ici, il est traité par le modal dédié
        if (key === "mot_de_passe") return;

        if (userData[key] !== undefined && userData[key] !== null) {
          jsonData[key] = userData[key];
        }
      });

      // Utiliser l'ID déjà présent dans userData (pas besoin de refaire un appel API)
      await updateUser(userData.id, jsonData);

      // Update global context for navbar
      setGlobalUserData(userData);

      toast({
        title: "Succès",
        description: `Section ${section} mise à jour`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la mise à jour",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Afficher un spinner pendant la vérification du token
  if (isLoading) {
    return <LoadingSpinner message="Vérification de la session..." size="md" />;
  }

  // Ne pas afficher le contenu si non authentifié
  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box minH="100vh" bg="#F9FAFB" p={{ base: 4, md: 8 }}>
      <Grid
        templateColumns={{ base: "1fr", lg: "280px 1fr" }}
        gap={8}
        maxW="1400px"
        mx="auto"
        mt={100}
        h={{ lg: "calc(100vh - 140px)" }} // Fixed height to allow internal scroll
      >
        {/* Sidebar */}
        <Box
          position="sticky"
          top="0"
          height="max-content"
          alignSelf="flex-start"
          zIndex="10"
          mt={12}
          bg="white"
          p={4}
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.100"
          shadow="sm"
          display={{ base: "none", lg: "block" }}
        >
          <Text fontSize="xs" fontWeight="700" color="gray.400" mb={4} px={0} letterSpacing="wider">
            PARAMÈTRES DE BASE
          </Text>
          <VStack align="stretch" spacing={1}>
            {sections.map(section => (
              <SidebarItem
                key={section.id}
                isActive={activeSection === section.id}
                onClick={() => {
                  const element = document.getElementById(section.id);
                  if (element) {
                    element.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    });
                  }
                }}
              >
                {section.label}
              </SidebarItem>
            ))}
          </VStack>
        </Box>

        {/* Content Area - SCROLLABLE */}
        <VStack
          id="profile-content-scroll"
          align="stretch"
          spacing={6}
          overflowY="auto"
          pr={2}
          css={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '10px' },
          }}
        >
          {/* Section: Paramètres de base / Email */}
          <Box id="email" bg="white" p={8} borderRadius="xl" border="1px solid" borderColor="gray.100">
            <Heading size="sm" mb={8} fontWeight="600" color="gray.800">Paramètres de base</Heading>

            <VStack spacing={8} align="stretch">
              {/* Photo Section */}
              <HStack spacing={4} align="center" justify="space-between">
                <HStack spacing={4}>
                  <Text fontWeight="600" fontSize="sm" color="gray.700">Photo</Text>
                  <Text fontSize="xs" color="gray.400">Image JPEG ou PNG de 150 x 150 px</Text>
                </HStack>
                <Box position="relative">
                  <Avatar size="xl" src={userData.photo} />
                  <Input
                    type="file"
                    id="photo-input"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    display="none"
                  />
                  <Box
                    position="absolute"
                    bottom="0"
                    right="0"
                    bg="blue.600"
                    p={1.5}
                    borderRadius="full"
                    color="white"
                    cursor="pointer"
                    border="2px solid white"
                    onClick={() => document.getElementById('photo-input').click()}
                  >
                    <Icon as={MdCameraAlt} w={3} h={3} />
                  </Box>
                </Box>
              </HStack>

              <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600" color="gray.800">Prénom</FormLabel>
                  <Input
                    name="prenom"
                    value={userData.prenom}
                    onChange={handleInputChange}
                    placeholder="Prénom"
                    h="48px"
                    borderRadius="lg"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600" color="gray.800">Nom</FormLabel>
                  <Input
                    name="nom"
                    value={userData.nom}
                    onChange={handleInputChange}
                    placeholder="Nom"
                    h="48px"
                    borderRadius="lg"
                  />
                </FormControl>
              </Grid>

              <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600" color="gray.800">Numéro de téléphone</FormLabel>
                  <InputGroup>
                    <InputLeftElement h="48px" w="70px">
                      <HStack spacing={1} cursor="pointer">
                        <Box w="20px" h="14px" bg="red.500" borderRadius="sm" />
                        <Text fontSize="xs" fontWeight="600">+212</Text>
                      </HStack>
                    </InputLeftElement>
                    <Input
                      name="téléphone"
                      value={userData.téléphone}
                      onChange={handleInputChange}
                      pl="75px"
                      placeholder="6 098 76 65 8"
                      h="48px"
                      borderRadius="lg"
                    />
                  </InputGroup>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600" color="gray.800">Email</FormLabel>
                  <Input
                    name="email"
                    value={userData.email}
                    onChange={handleInputChange}
                    placeholder="jason@studio.io"
                    h="48px"
                    borderRadius="lg"
                  />
                </FormControl>
              </Grid>

              <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600" color="gray.800">Ville</FormLabel>
                  <Select
                    name="ville"
                    value={userData.ville}
                    onChange={handleInputChange}
                    placeholder="Sélectionner une ville"
                    h="48px"
                    borderRadius="lg"
                  >
                    {villesMaroc.map((ville) => (
                      <option key={ville} value={ville}>{ville}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600" color="gray.800">Date de naissance</FormLabel>
                  <Input
                    name="date_naissance"
                    type="date"
                    value={userData.date_naissance}
                    onChange={handleInputChange}
                    h="48px"
                    borderRadius="lg"
                  />
                </FormControl>
              </Grid>

              <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600" color="gray.800">Langue</FormLabel>
                  <Select
                    name="langue"
                    value={userData.langue}
                    onChange={handleInputChange}
                    h="48px"
                    borderRadius="lg"
                  >
                    <option value="fr">Français</option>
                    <option value="ar">العربية (Arabe)</option>
                  </Select>
                </FormControl>
              </Grid>

              {/* Changer le mot de passe */}
              <Box
                p={4}
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                bg="gray.50"
                cursor="pointer"
                _hover={{ bg: "gray.100", borderColor: "blue.300" }}
                transition="all 0.2s"
                onClick={onChangePasswordOpen}
              >
                <HStack justify="space-between" align="center">
                  <HStack spacing={3}>
                    <Box p={2} bg="blue.100" borderRadius="lg">
                      <Icon as={MdLock} boxSize={5} color="blue.600" />
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="600" fontSize="sm" color="gray.800">Changer le mot de passe</Text>
                      <Text fontSize="xs" color="gray.500">Modifiez votre mot de passe sécurisé</Text>
                    </VStack>
                  </HStack>
                  <Icon as={MdChevronRight} boxSize={5} color="gray.400" />
                </HStack>
              </Box>

              <HStack justify="flex-end">
                <Button
                  bg="#006133"
                  color="white"
                  px={10}
                  h="44px"
                  borderRadius="lg"
                  onClick={() => handleSave("Profil")}
                >
                  Enregistrer
                </Button>
              </HStack>
            </VStack>
          </Box>

        </VStack>
      </Grid>

      {/* Modal pour changer le mot de passe */}
      <ChangePasswordModal isOpen={isChangePasswordOpen} onClose={onChangePasswordClose} />
    </Box>
  );
}
